import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Toaster } from "sonner";

import { setUser, useGetMeQuery, useLogoutMutation } from "@/features/auth";
import { useBuscarConfiguracaoSessaoQuery } from "@/features/configuracoes/configuracao-sessao/services/configuracaoSessaoApi";
import { Ativar2FAOffCanvas } from "@/features/meu-perfil/components/overlays/Ativar2FAOffCanvas";
import { RecoveryCodesOffCanvas } from "@/features/meu-perfil/components/overlays/RecoveryCodesOffCanvas";
import { baseApi } from "@/shared/api/baseApi";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { useInactivityTimeout } from "@/shared/hooks/useInactivityTimeout";
import { useAppDispatch } from "@/shared/store/hooks";

import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";

export function AppLayout() {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mfaSetupCodes, setMfaSetupCodes] = useState<string[]>([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);

  // Fecha o drawer ao redimensionar para desktop — derivado, sem effect
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [logout] = useLogoutMutation();

  // Busca configuração de sessão para timeout de inatividade
  const { data: sessaoConfig } = useBuscarConfiguracaoSessaoQuery(undefined, {
    pollingInterval: 5 * 60 * 1000, // 5 minutos
  });

  // /me — polling como rede de segurança (5 min).
  const { data: meData, refetch: refetchMe } = useGetMeQuery(undefined, {
    pollingInterval: 5 * 60 * 1000,
  });

  // Refetch do /me a cada troca de rota — garante permissões frescas e forcarLogout imediato.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      refetchMe();
    }
  }, [location.pathname, refetchMe]);

  const prevMeRef = useRef(meData);
  useEffect(() => {
    if (meData && meData !== prevMeRef.current) {
      const prev = prevMeRef.current;

      // Se o backend sinaliza forçar logout (perfil ou permissões alterados)
      if (meData.forcarLogout) {
        logout().then(() => navigate("/login"));
        return;
      }

      dispatch(setUser(meData));
      prevMeRef.current = meData;

      // Se as permissões mudaram, limpa todo o cache do RTK Query.
      if (
        prev &&
        JSON.stringify(prev.modulos) !== JSON.stringify(meData.modulos)
      ) {
        dispatch(baseApi.util.resetApiState());
      }
    }
  }, [meData, dispatch, logout, navigate]);

  const handleInactivityTimeout = useCallback(async () => {
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  useInactivityTimeout(
    sessaoConfig?.timeoutInatividade ?? 0,
    handleInactivityTimeout
  );

  const mustSetupMfa =
    !!meData && meData.mfa === false && meData.mfaObrigatorio === true;

  const handleMfaSetupSuccess = (codes: string[]) => {
    setMfaSetupCodes(codes);
    setShowRecoveryCodes(true);
  };

  const handleRecoveryClose = () => {
    setShowRecoveryCodes(false);
    void refetchMe();
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-50">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        isMobile={isMobile}
        mobileOpen={isMobile && mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar onMenuOpen={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="bottom-right" />

      {/* Bloqueio obrigatório de configuração de MFA */}
      {mustSetupMfa && !showRecoveryCodes && (
        <div className="fixed inset-0 z-[9998] bg-black/40" />
      )}
      <Ativar2FAOffCanvas
        isOpen={mustSetupMfa && !showRecoveryCodes}
        onClose={() => {}}
        onSuccess={handleMfaSetupSuccess}
      />
      <RecoveryCodesOffCanvas
        isOpen={showRecoveryCodes}
        onClose={handleRecoveryClose}
        recoveryCodes={mfaSetupCodes}
      />
    </div>
  );
}
